/* eslint-disable no-param-reassign */
import Phaser from 'phaser';
import { fadeMusicDown, fadeMusicUp, playTitleJingle } from '../SoundManager';
import ABCyaPhaser from '@abcya/abcya_phaser';
import gameData from '../gameData';
import QuestionGenerator from '../questions/QuestionGenerator';
import GameplayBackground from '../fbhop/GameplayBackground';
import Platform from '../sprites/Platform';
import CrumblePiece from '../sprites/CrumblePiece';
import CollectItem from '../sprites/CollectItem';
import AnswerBlockPhysics from '../sprites/AnswerBlockPhysics';
import PlayerInput from '../fbhop/PlayerInput';
import Hud from '../ui/Hud';
import EmitterManager from '../fbhop/EmitterManager';
import PlayerFuzzbug from '../fbhop/PlayerFuzzbug';
import QuestionDisplay from '../questions/QuestionDisplay';
import QuestionData from '../questions/QuestionData';
import PlatformQuestion from '../sprites/PlatformQuestion';
import ABCyaLib from '@abcya/abcya_lib';

export default class PlayScene extends Phaser.Scene {
  static get KEY() { return 'PlayScene'; }

  constructor() {
    super({ key: PlayScene.KEY });

    this.platformLevels = [
      { spacing: 200, minX: 240, maxX: 320, crumble: 0, moving: 0, varYMin: 0, varYMax: 40, platformWidth1: 2, platformWidth2: 3 },
      { spacing: 240, minX: 240, maxX: 360, crumble: 0, moving: 0, varYMin: 150, varYMax: 40, platformWidth1: 2, platformWidth2: 3 },
      { spacing: 200, minX: 240, maxX: 360, crumble: 0, moving: 0, varYMin: 200, varYMax: 40, platformWidth1: 2, platformWidth2: 3 }
    ];

    this.platFormsInfo = [
      { spritename: 'large_platform' },
      { spritename: 'medium_platform' },
      { spritename: 'small_platform' }
    ];
  }

  init () {
    playTitleJingle();

    this.jumpSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('bounce');
    this.fallSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('fall');
    this.yay = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);// game.add.audio('yay');
    this.crumbleSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('crumble');
    this.hatPickupSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('hat_glasses_pickup');
    this.coolSound = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);// game.add.audio('cool');
    this.niceSound = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);// game.add.audio('nice');
    this.correctSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('correct_answer');
    this.incorrectSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('incorrect_answer');
    this.paintSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('paint_pickup');
    this.woahSound = ABCyaPhaser.Audio.createSound(this, 1, false, 0, ABCyaPhaser.Audio.LOCALIZATION_AUDIO_SPRITE);// game.add.audio('woah');
    this.partyhornSound = ABCyaPhaser.Audio.createSound(this);// game.add.audio('partyhorn');

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.jumpSound.destroy();
      this.fallSound.destroy();
      this.yay.destroy();
      this.crumbleSound.destroy();
      this.hatPickupSound.destroy();
      this.coolSound.destroy();
      this.niceSound.destroy();
      this.correctSound.destroy();
      this.incorrectSound.destroy();
      this.paintSound.destroy();
      this.woahSound.destroy();
      this.partyhornSound.destroy();
    });

    this.physics.world.on('worldstep', () => this.checkForCollisions());

    if (ABCyaLib.Environment.instance.environment === ABCyaLib.Environment.ENVIRONMENTS.staging) {
      this.input.keyboard.on(`${Phaser.Input.Keyboard.Events.KEY_DOWN}SPACE`, () => {
        gameData.config.justDoQuestions = !gameData.config.justDoQuestions;
      });
    }
  }

  create() {
    this.playStateId = 0;

    this.minPlatformSpread = 240;
    this.maxPlatformSpread = 320;
    this.lastPlatformSpawnPt = { x: 0, y: 0, moving: false, crumbles: false };
    this.curLevel = 1;
    this.questionsAnswered = 0;
    this.platformSpawnInfo = this.platformLevels[this.curLevel];

    this.spawnInfo = {
      crumbleChance: Math.min(this.questionsAnswered * 0.05, 0.91),
      moveChance: Math.min(this.questionsAnswered * 0.025, 0.91),
      sizeMax: 1,
      sizeChance: ['large_platform', 'medium_platform', 'small_platform'],
      hatSpawned: false,
      glassesSpawned: false,
      brushSpawned: false,
      itemOffset: { x: 0,y: -20 },
      spaceItem: false
    };
    this.questionMode = false;
    this.waitingOnQuestion = false;
    this.questionPlatformSpawned = false;
    this.platformsUntilQuestion = gameData.config.questionSpacing;

    this.isClimbing = true;

    this.jumpPower = gameData.config.jumpHeight;
    this.platformSpacing = gameData.config.platformSpacing;

    this.distanceFromGround = 0;
    this.horizontalPlayerSpeed = 400;

    this.gameover = false;
    this.movedGround = false;

    this.itemsCollected = {
      hats: 0,
      glasses: 0,
      brushes: 0
    };

    this.physics.world.checkCollision.down = false;

    for (let i = 0; i < this.platFormsInfo.length; i++) {
      const frameInfo = this.textures.getFrame(ABCyaPhaser.Images.DEFAULT_ATLAS, this.platFormsInfo[i].spritename);
      this.platFormsInfo[i].width = frameInfo.width;
      this.platFormsInfo[i].height = frameInfo.height;
      this.platFormsInfo[i].halfWidth = frameInfo.width / 2;
      this.platFormsInfo[i].halfHeight = frameInfo.height / 2;
      let x1 = this.scale.width - frameInfo.width;
      let x2 = this.scale.width * 0.5 - frameInfo.width / 2;
      const x3 = 0;
      let sl = [x1,x2,x3];
      this.platFormsInfo[i].spawnLocsOut = sl;
      x1 = this.scale.width * 0.25 - frameInfo.width / 2;
      x2 = this.scale.width * 0.75 - frameInfo.width / 2;
      sl = [x1, x2];
      this.platFormsInfo[i].spawnLocsIn = sl;
      this.platFormsInfo[i].rightLimit = this.scale.width - frameInfo.width;
      this.platFormsInfo[i].spawnOffsetMin = this.platformLevels[i].varYMin;
    }

    this.jumpPowerWaiter = 0;
    this.waitingJumpPower = 0;

    this.questionDisplay = new QuestionDisplay(this, 0,158);
    this.add.existing(this.questionDisplay);
    this.questionGenerator = new QuestionGenerator(QuestionData, this);

    this.bg = new GameplayBackground(this, 0,0);
    this.add.existing(this.bg);

    this.ground = ABCyaPhaser.Images.addImage(0, 500, 'PlatformQuestion', this).setOrigin(0);
    this.ground.y = this.scale.height - this.ground.height;
    this.physics.add.existing(this.ground);
    this.ground.inPlace = false;

    this.platformGroup = this.add.container();
    const platformPoolCount = 40;
    for (let i = 0; i < platformPoolCount; i++) {
      const item = new Platform(this, 0, -200, this.platformGroup);
    }

    this.crumbleGroup = this.add.container();
    for (let i = 0; i < 80; i++) {
      const item = new CrumblePiece(this, 0, 0, this.crumbleGroup);
      item.despawn();
    }

    this.itemsGroup = this.add.container();
    for (let i = 0; i < platformPoolCount; i++) {
      const item = new CollectItem(this, 0, -200, this.itemsGroup);
    }

    this.answerBlockGroup = new AnswerBlockPhysics(this, 0, 0);
    this.add.existing(this.answerBlockGroup);

    this.inputControls = new PlayerInput(this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.inputControls.cleanup());

    this.questionPlatform = new PlatformQuestion(this,0, 190);
    this.questionPlatform.questionDisplayRef = this.questionDisplay;
    this.add.existing(this.questionPlatform);

    this.hud = new Hud(this, 0,0);
    this.hud.on('ARROW_CLICKED', this.inputControls.clickInput, this.inputControls);
    this.hud.on('START_OVER', this.startOver, this);
    this.add.existing(this.hud);

    this.particleGroup = new EmitterManager(this);

    const startupInfo = this.startupLevel();
    this.distanceToNextPlatform = startupInfo.lastY;
    let playerStartX = startupInfo.firstX - this.scale.width / 2;
    if (playerStartX > 0) {
      playerStartX = playerStartX;
    }
    else {
      playerStartX = this.scale.width - (playerStartX * -1);
    }
    this.player = new PlayerFuzzbug(this, playerStartX, 945);
    this.add.existing(this.player);

    this.children.sendToBack(this.bg);
    this.children.bringToTop(this.itemsGroup);
    this.children.bringToTop(this.platformGroup);
    this.children.bringToTop(this.ground);
    this.children.bringToTop(this.player);
    this.questionDisplay.visible = false;

    this.shade = ABCyaPhaser.Images.addImage(0,0, 'shades_overlay_dot', this).setOrigin(0);
    this.shade.displayWidth = this.scale.width;
    this.shade.displayHeight = this.scale.height;
    this.shade.setActive(false).setVisible(false);

    this.children.bringToTop(this.questionDisplay);
    this.children.bringToTop(this.hud);

    this.boostTimer = -1;

    this.hud.flashScreen();
  }

  startupLevel () {
    let x = 0;
    let y = 800;
    const howmany = Math.ceil(y / this.platformSpacing);
    const di = this.platFormsInfo[this.curLevel];
    let firstX = null;
    for (let i = 0; i < howmany; i++) {
      const selectFrom = Phaser.Math.Between(0,100) > 50 ? di.spawnLocsOut : di.spawnLocsIn;
      x = selectFrom[Phaser.Math.Between(0,selectFrom.length - 1)];
      if (firstX === null) {
        firstX = x;
      }

      this.spawnPlatform(x, y);
      y -= this.platformSpacing;
    }

    this.questionDisplay.setQuestion(this.questionGenerator.currentQuestion);

    this.setYLimit(438);
    this.spawningPlatforms = true;
    return { lastY: y - this.platformSpacing, firstX };
  }

  resumeSpawnAfterQuestionWithPremadePlatforms () {
    let sm = 1;
    if (this.questionsAnswered > 2) {
      sm = 2;
    }
    else if (this.questionsAnswered > 4) {
      sm = 3;
    }
    this.spawnInfo = {
      crumbleChance: Math.min(this.questionsAnswered * 0.05, 0.91),
      moveChance: Math.min(this.questionsAnswered * 0.025, 0.91),
      sizeMax: sm,
      sizeChance: ['large_platform', 'medium_platform', 'small_platform'],
      hatSpawned: false,
      glassesSpawned: false,
      brushSpawned: false,
      itemOffset: { x: 0,y: -20 },
      spaceItem: false
    };

    this.questionMode = false;
    this.questionPlatform.despawning = true;
    this.questionPlatformSpawned = false;
    const startY = this.questionDisplay.y;
    this.setYLimit(438);
    if (!gameData.config.justDoQuestions) {
      this.spawnPlatform(0, this.questionDisplay.y, 0, false, false, false);
      this.spawnPlatform(0, this.questionDisplay.y, 0, false, false, false);
      this.spawnPlatform(210, this.questionDisplay.y, 0, false, false, false);
      this.spawnPlatform(this.scale.width / 2, this.questionDisplay.y, 0, false, false, false);
      this.spawnPlatform(this.scale.width - 210, this.questionDisplay.y, 0, false, false, false);
    }

    this.distanceToNextPlatform = startY - this.platformSpacing;
    this.questionPlatform.isActive = true;
    this.isClimbing = true;
    this.waitingJumpPower = gameData.config.jumpHeight * 2.5;
    this.jumpPowerWaiter = 0.2;
    this.spawningPlatforms = false;
    this.platformsUntilQuestion = gameData.config.questionSpacing;

    this.player.lockedHorizontal = false;
    this.player.spriteParts.backToHappy();

    this.answerBlockGroup.deactivate();
    this.setYLimit(438 + 20);
    this.boostTimer = 1.5;
    this.touchedAfterBoost = false;

    if (gameData.config.justDoQuestions) {
      this.waitingJumpPower = gameData.config.jumpHeight * 0.1;
      this.boostTimer = 0;
      this.questionDisplay.setQuestion(this.questionGenerator.currentQuestion);
      this.jumpPower = 0.1;
      this.questionMode = true;
      this.questionPlatform.despawning = false;
      this.questionPlatformSpawned = true;
    }
  }

  setJumpPower (value) {
    this.jumpPower = value;
  }

  resetQuestionMode() {
    this.platformsUntilQuestion = gameData.config.questionSpacing * 2;
    this.questionPlatformSpawned = false;
    this.waitingOnQuestion = false;
    this.questionPlatform.isActive = false;
    this.questionPlatform.inPlace = false;
    this.isClimbing = true;
    this.questionMode = false;
  }

  setYLimit (value) {
    this.yLimit = value;
  }

  inputAxisX (value, isSpeedValue) {
    let amt = value;
    if (!isSpeedValue) amt = amt * this.horizontalPlayerSpeed;
    this.player.setVelX(amt);
  }

  spawnPlatform (x,y, startVY, crumble, moving, needsJumpBoost, size) {
    if (crumble === void 0) crumble = false;
    if (size === void 0) size = 'large_platform';
    if (moving === void 0) moving = false;
    const item = this.platformGroup.getFirst('active', false);
    let jumpMod = 1.0;
    if (needsJumpBoost) {
      jumpMod = 1.5;
    }
    item.spawn(x,y, startVY, size, size, crumble, moving, jumpMod);
    return item;
  }

  forceSpawnCollectItem (x,y) {
    const item = this.itemsGroup.getFirst('active', false);

    const type = Phaser.Utils.Array.GetRandom(gameData.itemsToCollect);
    item.spawn(x,y, type);
  }

  spawnHat (x,y) {
    const type = Phaser.Utils.Array.GetRandom(gameData.hats);
    const item = this.itemsGroup.getFirst('active',false);
    item.spawn(x,y, type);
  }

  spawnGlasses (x,y) {
    const type = Phaser.Utils.Array.GetRandom(gameData.glasses);
    const item = this.itemsGroup.getFirst('active',false);
    item.spawn(x,y, type);
  }

  spawnBrush (x,y) {
    const type = Phaser.Utils.Array.GetRandom(gameData.brushes);
    const item = this.itemsGroup.getFirst('active',false);
    item.spawn(x,y, type);
  }

  makeCrumble (x,y) {
    let pieces = null;

    pieces = gameData.gameProps.pieces1;

    const len = pieces.length;
    for (let i = 0; i < len; i++) {
      const item = this.crumbleGroup.getFirst('active',false);
      const oX = pieces[i].x;
      const oY = pieces[i].y;
      if (item) {
        const vx = pieces[i].vx * 10 + (-this.player.feet.body.velocity.x * 0.13);
        const vy = pieces[i].vy * 10;
        item.spawn(x + oX,y + oY, pieces[i].spriteId, vx,vy, 2000);
      }
      else {
        console.error('ran out of objects');
      }
    }
  }

  checkClimbDistance () {
    let dist = 0;
    if (this.player.getBodyY() <= this.yLimit) {
      const pvy = this.player.getVelY();
      if (pvy <= 0) {
        /*
        Running in Chrome on a Mac with Sonoma and an Apple chip causes an issue where this.player.getBodyY() 
        sometimes returns the same as this.yLimit/this.player.getPrevBodyY(). For some reason this does not 
        happen on other chips, so I assume there is a rounding difference or something. The result 
        is a lot of 0 distances returned, which causes problems downstream. The quick hack fix is to keep the 
        this.player.getBodyY() to just under the this.yLimit value so 0 is never returned. An issue will be created
        to investigate this further.
        */
        dist = this.player.getPrevBodyY() - Math.min(this.yLimit - 0.5, this.player.getBodyY());
      }
    }
    return dist;
  }

  checkForCollisions () {
    if (this.player.canHitGround === true) {
      this.physics.overlap(this.player.feet, this.ground, this.onPlayerHitGround, null, this);
    }

    // Platform collisions
    if (this.player.canHitPlatforms === true) {
      this.physics.collide(this.player.feet, this.platformGroup.list, this.onPlatformHit, null, this);
    }

    if (this.questionPlatform.isActive === true) {
      this.physics.collide(this.player.feet, this.questionPlatform, this.onQuestionPlatformHit, null, this);
      if (this.answerBlockGroup.collidesPlayer) {
        this.physics.collide(this.player.feet, this.answerBlockGroup.list, this.onQuestionBlockHit, null, this);
      }
    }
    else {
      if (this.gameover) {
        this.physics.collide(this.answerBlockGroup.blocks, this.questionPlatform, this.onBlockPlatformHit, null, this);
        if (this.answerBlockGroup.collidesPlayer) {
          this.physics.collide(this.player.feet, this.answerBlockGroup.list, this.onQuestionBlockHit, null, this);
        }
      }
      this.physics.collide(this.player.feet, this.questionPlatform, this.onQuestionPlatformHit, null, this);
      if (this.answerBlockGroup.collidesPlayer) {
        this.physics.collide(this.player.feet, this.answerBlockGroup.list, this.onQuestionBlockHit, null, this);
      }
    }

    this.physics.collide(this.player.fakeFuzz, this.itemsGroup.list, this.onItemCollect, null, this);
  }

  onPlayerHitGround (obj1, obj2) {
    if (!this.gameover) {
      if (this.distanceFromGround > 0) {
        this.doDie();
      }
      else {
        this.player.jump(-this.jumpPower);
        ABCyaPhaser.Audio.playSound('Bounce', this.jumpSound);
      }
    }
  }

  onPlatformHit (obj1, obj2) {
    if (obj1.body.touching.down) {
      if (this.movedGround === false) {
        this.movedGround = true;
      }
      this.player.checkForHatEffect();
      if (this.player.isGoofy && this.particleGroup) this.particleGroup.showJester(this.player.feet.body.x + 13, this.player.feet.body.y - 84);

      this.player.jump(-this.jumpPower * obj2.jumpMod);
      if (obj2.crumbles) {
        this.makeCrumble(obj2.body.x, obj2.body.y, obj2.sizeId);
        obj2.doCrumble();
        if (this.particleGroup) this.particleGroup.makeSmokePuff(obj2.body.x + obj2.width / 2, obj2.body.y);
        ABCyaPhaser.Audio.playSound('Crumble', this.crumbleSound);
      }
      else {
        ABCyaPhaser.Audio.playSound('Bounce', this.jumpSound);
      }
      this.touchedAfterBoost = true;
    }
  }

  onQuestionPlatformHit (obj1, obj2) {
    if (obj1.body.touching.down) {
      if (this.gameover) {
        this.player.jump(0);
      }
      if (this.questionPlatform.inPlace) {
        this.player.backToNormalClimbSettings();
      }
      else {
        this.yLimit = -200;
        this.isClimbing = false;
      }
      this.player.freeJumping = false;
      this.player.jump(-this.jumpPower);
      this.questionPlatform.jumpedOn = true;
    }
  }

  onQuestionBlockHit (obj1, obj2) {
    if (obj1.body.touching.down) {
      this.player.jump(-this.jumpPower);
    }
  }

  onBlockPlatformHit (obj1, obj2) {
    obj1.body.velocity.y = -10;
  }

  onItemCollect (obj1, obj2) {
    const itemtype = obj2.itemData.type;

    if (itemtype === 'hat') {
      this.itemsCollected.hats++;
      this.player.collectHat(obj2.itemData);
      if (this.player.isPartying) {
        this.particleGroup.showParty(this.player.feet.body.x + 13, this.player.feet.body.y - 84);
        ABCyaPhaser.Audio.playSound('party_horn_01', this.partyhornSound);
      }
      ABCyaPhaser.Audio.playSound('Hat_Glasses', this.hatPickupSound);
      if (Phaser.Math.Between(0,100) > 50) {
        ABCyaPhaser.Audio.playSound('Nice', this.niceSound);
      }
      else {
        ABCyaPhaser.Audio.playSound('Cool', this.coolSound);
      }
    }
    else if (itemtype === 'glasses') {
      this.itemsCollected.glasses++;
      if (obj2.itemData.texture === 'purple_glasses') {
        this.shade.setActive(true).setVisible(true);
        ABCyaPhaser.Audio.playSound('bug_woah', this.woahSound);
      }
      else {
        this.shade.setActive(false).setVisible(false);
        if (Phaser.Math.Between(0,100) > 50) {
          ABCyaPhaser.Audio.playSound('Cool', this.coolSound);
        }
        else {
          ABCyaPhaser.Audio.playSound('Nice', this.niceSound);
        }
      }

      ABCyaPhaser.Audio.playSound('Hat_Glasses', this.hatPickupSound);
      this.player.spriteParts.showGlasses(obj2.itemData);
    }
    else if (itemtype === 'brush') {
      this.itemsCollected.brushes++;
      if (Phaser.Math.Between(0,100) > 50) {
        ABCyaPhaser.Audio.playSound('Cool', this.coolSound);
      }
      else {
        ABCyaPhaser.Audio.playSound('Nice', this.niceSound);
      }
      ABCyaPhaser.Audio.playSound('Paint_Brush', this.paintSound);
      this.particleGroup.sprayColor(obj1.x , obj1.y, obj2.itemData.value);
      this.player.spriteParts.splash(obj2.itemData.color);
      this.player.spriteParts.changeColor(obj2.itemData);
    }

    obj2.setActive(false).setVisible(false);
    obj2.body.enable = false;
  }

  update (time, dt) {
    // INPUT CHECK
    this.inputControls.checkInput();

    const pvy = this.player.getVelY();
    if (this.boostTimer > 0) {
      this.boostTimer -= dt / 1000;
      if (this.boostTimer < 0.5) {
        this.spawningPlatforms = true;
        this.jumpPower = gameData.config.jumpHeight;
      }
    }

    // CLIMBING CHECK
    if (this.isClimbing === true) {
      this.updateClimbing(pvy);
    }
    else {
      this.updateNotClimbing(pvy);
    }

    if (this.waitingJumpPower > 0) {
      this.jumpPowerWaiter -= dt / 1000;
      if (this.jumpPowerWaiter <= 0) {
        this.jumpPower = this.waitingJumpPower;
        this.waitingJumpPower = -1;
        this.player.setFreeJumping();
        this.isClimbing = true;
      }
    }
  }

  updateClimbing (pvy) {
    const dist = this.checkClimbDistance();
    if (dist > 0) {
      this.distanceFromGround += dist;
      this.bg.doClimb(dist, this.questionBoosting);
      this.hud.addToScore(~~(dist * 0.5));

      this.player.pushToPrevY(this.yLimit);
      this.distanceToNextPlatform -= dist;

      if (this.spawningPlatforms) {
        if (!this.questionPlatformSpawned && !this.waitingOnQuestion) {
          const counts = this.touchedAfterBoost;
          const spawnedPlatform = this.checkPlatformSpawn(pvy, this.platformsUntilQuestion <= 1, counts);
          if (spawnedPlatform === true && counts) this.platformsUntilQuestion--;
          if (this.platformsUntilQuestion <= 0) {
            this.waitingOnQuestion = true;
            this.questionPlatformSpawned = false;
            this.questionDisplay.setQuestion(this.questionGenerator.currentQuestion);
          }
        }
        else if (!this.questionPlatformSpawned) {
          const spawnedPlatform = this.checkForQuestionSpawn(pvy);
          if (spawnedPlatform === true) {
            this.waitingOnQuestion = false;
            this.questionPlatformSpawned = true;
            this.questionDisplay.visible = true;
          }
        }
      }
    }

    const newvel = this.getVelocityForDistance(dist, pvy);

    if (!this.ground.inPlace) {
      if (this.ground.body.y >= this.scale.height + 200) {
        this.ground.body.velocity.y = 0;
        this.ground.inPlace = true;
      }
      else {
        this.ground.body.velocity.y = newvel;
      }
    }

    this.updatePlatforms(newvel);

    if (this.questionPlatform.isActive) {
      this.questionPlatform.body.velocity.y = newvel;
      this.questionDisplay.y = this.questionPlatform.body.y - 750;
      if (!this.questionPlatform.despawning && this.questionPlatform.inPlace === true) {
        this.isClimbing = false;

        this.player.capClimb();
      }
      this.questionPlatform.body.velocity.y = newvel;
    }
    else {
      this.questionPlatform.body.velocity.y = newvel;
    }

    this.updateItems(newvel);
    this.cleanUpTheDead();
  }

  updateNotClimbing (pvy) {
    if (!this.questionMode) {
      const newvel = this.getVelocityForFreeJump(pvy);
      this.updatePlatforms(newvel);
      this.questionPlatform.body.velocity.y = newvel;
      this.updateItems(newvel);
    }

    if (this.questionPlatform.inPlace === true && !this.questionDisplay.beenAnswered) {
      if (!this.questionMode) {
        this.player.capClimb();
        this.jumpPower = 200;
        fadeMusicDown(this, 0.2);
      }
      this.questionMode = true;
    }
  }

  checkPlatformSpawn (pvy, extraJump, checkItemSpawn) {
    if (checkItemSpawn === void 0) checkItemSpawn = true;
    if (this.distanceToNextPlatform <= 0) {
      let x = this.scale.width - 210;// large_platform
      const y = -50;
      const di = this.platFormsInfo[this.curLevel];

      const selectFrom = Phaser.Math.Between(0,100) > 50 ? di.spawnLocsOut : di.spawnLocsIn;
      const infoIndex = Phaser.Math.Between(0,selectFrom.length - 1);
      x = selectFrom[infoIndex];
      let c = false;
      let m = false;
      let item = null;
      let size = null;
      if (Phaser.Math.Between(1,100) < this.spawnInfo.crumbleChance * 100) {
        c = true;
      }
      if (Phaser.Math.Between(1,100) < this.spawnInfo.moveChance * 10) {
        m = true;
      }
      if (!c) {
        size = this.spawnInfo.sizeChance[Phaser.Math.Between(1, this.spawnInfo.sizeMax) - 1];
      }
      item = this.spawnPlatform(x, y, -pvy, c, m, extraJump, size);

      if (checkItemSpawn) {
        let spawnedItem = false;
        if (!this.spawnInfo.hatSpawned) {
          if (this.platformsUntilQuestion < 3 || Phaser.Math.Between(0,100) > 62) {
            this.spawnHat(x + item.width * 0.5, y + this.spawnInfo.itemOffset.y);
            this.spawnInfo.hatSpawned = true;
            spawnedItem = true;
          }
        }
        else if (!this.spawnInfo.glassesSpawned) {
          if (this.platformsUntilQuestion < 3 || Phaser.Math.Between(0,100) > 62) {
            this.spawnGlasses(x + item.width * 0.5, y + this.spawnInfo.itemOffset.y);
            this.spawnInfo.glassesSpawned = true;
            spawnedItem = true;
          }
        }
        if (!spawnedItem) {
          if (!this.spawnInfo.brushSpawned) {
            if (this.platformsUntilQuestion < 5 || Phaser.Math.Between(0,100) > 59) {
              this.spawnBrush(x + item.width * 0.5, y + this.spawnInfo.itemOffset.y);
              this.spawnInfo.brushSpawned = true;
              spawnedItem = true;
            }
          }
        }
      }

      if (Phaser.Math.Between(0,100) > 62) {
        const spread = Phaser.Math.Between(this.minPlatformSpread, this.maxPlatformSpread);

        let x2 = x + spread;
        if (x2 > di.rightLimit) {
          x2 = x2 - di.rightLimit;
          x2 *= Phaser.Math.Between(0,100) * 0.01;
        }
        item = this.spawnPlatform(x2, y, -pvy, c, m, extraJump);
      }

      this.distanceToNextPlatform = this.platformSpacing + this.questionsAnswered * 2;

      return true;
    }
    else {
      return false;
    }
  }

  checkForQuestionSpawn (pvy) {
    if (this.distanceToNextPlatform <= 0) {
      this.questionPlatform.spawn(-this.scale.width / 2,-50, -pvy);
      return true;
    }
    else {
      return false;
    }
  }

  updatePlatforms (newvel) {
    this.platformGroup.iterate((item) => {
      if (item.active) item.body.velocity.y = newvel;
    });
  }

  updateItems (newvel) {
    this.itemsGroup.iterate((item) => {
      if (item.active)item.body.velocity.y = newvel;
    });
    this.crumbleGroup.iterate((item) => {
      if (item.active && item.body.velocity.y < newvel) item.body.velocity.y += newvel * 0.15;
    });
  }

  cleanUpTheDead () {
    this.platformGroup.iterate((item) => {
      if (item.active && item.body.y > this.scale.height) {
        item.setActive(false).setVisible(false);
        item.body.enable = false;
      }
    });

    this.itemsGroup.iterate((item) => {
      if (item.active && item.body.y > this.scale.height) {
        item.setActive(false).setVisible(false);
        item.body.enable = false;
      }
    });
  }

  getVelocityForDistance (distanceMoved, pvy) {
    let newvel = -pvy;
    if (distanceMoved <= 0) {
      newvel = 0;
    }
    return newvel;
  }

  getVelocityForFreeJump (pvy) {
    let newvel = -pvy;
    if (pvy >= 0) {
      newvel = 0;
    }
    return newvel;
  }

  setNormalJump () {
    this.jumpPower = gameData.config.jumpHeight;
  }

  choiceSelected (toX, toY, isCorrect) {
    if (isCorrect) {
      ABCyaPhaser.Audio.playSound('Correct_Answer', this.correctSound);
    }
    else {
      ABCyaPhaser.Audio.playSound('Incorrect_Answer', this.incorrectSound);
    }
  }

  questionFeedbackFinished () {
    if (this.questionDisplay.selectedWasCorrect) {
      this.questionsAnswered++;

      this.player.spriteParts.celebrate();
      if (this.player.isPartying && this.particleGroup) {
        ABCyaPhaser.Audio.playSound('party_horn_01', this.partyhornSound);
        this.particleGroup.showParty(this.player.feet.body.x + 13, this.player.feet.body.y - 84);
      }
      else {
        ABCyaPhaser.Audio.playSound('yay', this.yay);
      }
      this.questionGenerator.generateNewQuestion();
      this.time.delayedCall(600, this.moveOnFromQuestion,null, this);
    }
    else {
      this.player.spriteParts.makeSad();
      if (gameData.config.justDoQuestions) {
        this.questionGenerator.generateNewQuestion();
        this.time.delayedCall(600, this.moveOnFromQuestion,null, this);
      }
      else {
        this.gameover = true;
        this.time.delayedCall(1100, this.doGameOver,null, this);
      }
    }
  }

  doGameOver () {
    fadeMusicUp(this);
    const locations = this.questionDisplay.getBlockPostions();
    this.answerBlockGroup.positionAndActivate(locations, this.questionDisplay.selectedIndex,this.questionDisplay.selectedWasCorrect);
    this.player.lockedHorizontal = false;
    this.jumpPower = gameData.config.jumpHeight * 0.2;
    this.questionDisplay.hideBoxesAndDetach();
    this.questionDisplay.doQuestionOutro();
    this.questionPlatform.setGravity(500);
    this.answerBlockGroup.collapse();
    this.time.delayedCall(1000, () => {
      this.hud.showGameOver(this.itemsCollected);
    });
  }

  doDie() {
    ABCyaPhaser.Audio.playSound('Fall', this.fallSound);
    this.gameover = true;
    this.hud.showGameOver(this.itemsCollected);
  }

  moveOnFromQuestion () {
    fadeMusicUp(this);
    this.jumpPower = gameData.config.jumpHeight * 0.2;
    this.resumeSpawnAfterQuestionWithPremadePlatforms();
  }

  startOver () {
    this.scene.start();
  }
}